/**
 * Bottom-right minimap: shrunken outline of the focal space with a viewport
 * indicator you can click to re-centre the main view.
 *
 * Perf contract: the scene (frame + item shapes) is memoized on the focal /
 * selection / highlight inputs and must NEVER re-render during a pan/zoom
 * gesture — rebuilding O(items) SVG shapes per frame was the single largest
 * steady-state gesture cost (see the 2026-07-07 board-gesture-performance
 * plan). Only the tiny viewport rectangle tracks the camera, by subscribing
 * to the viewport store directly.
 */
import { memo, useCallback, useEffect, useRef } from "react";
import { useContextMenu, type ContextMenuItem } from "@archlang/ui";
import type { BoardItem, BoardSpace } from "@archlang/scene/board";
import { panFor, logicalKeyOf, FOCUS_GROUND_ALPHA } from "@archlang/scene/board";
import {
  extractWidgetSpec,
  propValueToAttr,
  type Module,
} from "@archlang/scene/arch-model";
import { useNodeColoring } from "./coloring/useNodeColoring.js";
import { useBoardFocus } from "./focus/useBoardFocus.js";
import type { ViewportStore } from "./viewportStore";

const DEFAULT_FILL = "#475569";

// The node's base form — mirror the widget's shape so the minimap reads as
// the real diagram. Only the round/ellipse vs rounded-rect distinction is
// meaningful at minimap scale; custom widget chrome collapses to its box.
function nodeShape(meta: unknown): "ellipse" | "rect" {
  const m = meta as Module | undefined;
  if (!m || typeof m !== "object" || !("fields" in m)) return "rect";
  try {
    const shape = extractWidgetSpec(m).props.get("shape");
    if (shape && propValueToAttr(shape) === "ellipse") return "ellipse";
  } catch {
    /* malformed widget spec — fall back to a box */
  }
  return "rect";
}

interface MinimapProps {
  readonly focal: BoardSpace;
  readonly selectedId: string | null;
  readonly highlighted: ReadonlySet<string>;
  /** Pixel size of the main viewport container (for the viewport rect).
   *  Must be a referentially-stable object (BoardCanvas's ResizeObserver
   *  state), NOT a fresh measurement per render — it is a memo key. */
  readonly screen: { width: number; height: number };
  /** The live camera. The minimap reads it OUTSIDE the React render of the
   *  scene: only the viewport-rect child subscribes. */
  readonly store: ViewportStore;
  /** Called when the user clicks or drags the minimap to recentre. Target is
   *  in focal-space coordinates (centre of desired viewport). */
  readonly onCenter: (fx: number, fy: number) => void;
  /** Pixel size of the minimap itself. Defaults to the desktop 140×98;
   *  the mobile board passes a smaller one. */
  readonly width?: number;
  readonly height?: number;
  /** Card styling override — the mobile board passes its glass treatment. */
  readonly className?: string;
}

const MM_W = 140;
const MM_H = 98;

export const Minimap = memo(function Minimap({
  focal,
  selectedId,
  highlighted,
  screen,
  store,
  onCenter,
  width = MM_W,
  height = MM_H,
  className,
}: MinimapProps) {
  const ref = useRef<SVGSVGElement>(null);
  const isDragging = useRef(false);
  const coloring = useNodeColoring();
  const boardFocus = useBoardFocus();
  const menu = useContextMenu();

  // Right-click the minimap → recentre the main view on the focal space's
  // centre, reusing the same `onCenter` channel a click/drag uses. No new
  // handler is invented: the menu is a discoverable name for "reset camera to
  // this space".
  const onMenu = useCallback(
    (e: React.MouseEvent) => {
      const items: ContextMenuItem[] = [
        {
          label: "Center on space",
          onClick: () =>
            onCenter(
              focal.originX + focal.width / 2,
              focal.originY + focal.height / 2,
            ),
        },
      ];
      menu.open(e, items);
    },
    [menu, onCenter, focal.originX, focal.originY, focal.width, focal.height],
  );

  const scale = Math.min(width / focal.width, height / focal.height);
  const innerW = focal.width * scale;
  const innerH = focal.height * scale;
  const offsetX = (width - innerW) / 2;
  const offsetY = (height - innerH) / 2;
  // Frame represents focal coords originX..originX+width — subtract origin
  // when projecting focal-space points onto the minimap.
  const ox = focal.originX;
  const oy = focal.originY;

  const handleTo = useCallback(
    (e: React.PointerEvent) => {
      const el = ref.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      const fx = ox + (mx - offsetX) / scale;
      const fy = oy + (my - offsetY) / scale;
      onCenter(fx, fy);
    },
    [offsetX, offsetY, scale, ox, oy, onCenter],
  );

  const onPointerDown = (e: React.PointerEvent) => {
    e.stopPropagation();
    isDragging.current = true;
    ref.current?.setPointerCapture(e.pointerId);
    handleTo(e);
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!isDragging.current) return;
    e.stopPropagation();
    handleTo(e);
  };
  const onPointerUp = (e: React.PointerEvent) => {
    isDragging.current = false;
    try { ref.current?.releasePointerCapture(e.pointerId); } catch { /* ok */ }
  };

  return (
    <>
    <svg
      ref={ref}
      onContextMenu={onMenu}
      className={
        className ??
        // Near-opaque instead of backdrop-blur: the blur re-samples whatever
        // moves behind the minimap on every gesture frame (compositor cost).
        "rounded-lg bg-slate-900/(--tint-solid) border border-slate-700 shadow-lg cursor-crosshair"
      }
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      {/* Frame outline of the focal space */}
      <rect
        x={offsetX}
        y={offsetY}
        width={innerW}
        height={innerH}
        fill="rgba(30,31,34,0.8)"
        stroke="rgba(111,115,122,0.6)"
        strokeWidth={1}
        rx={3}
      />
      {/* Items */}
      {focal.items.map((item: BoardItem) => {
        // A `replicated` copy shares its logical place's selection + colour, so
        // every copy co-highlights (doppelgänger identity).
        const logicalKey = logicalKeyOf(item);
        const sel = logicalKey === selectedId;
        const hi = highlighted.has(logicalKey) || highlighted.has(item.id);
        // Unified node colour (aspect/label or diff) — same source the board
        // strokes with, so the minimap mirrors it.
        const nodeColor = coloring?.colorForNode(logicalKey);
        const baseFill = sel ? "#3574f0" : hi ? "#f5c85c" : nodeColor ?? DEFAULT_FILL;
        // View `focus`: fill-OPACITY, not colour. An uncoloured node is one flat
        // DEFAULT_FILL here, so there is neither chroma to withdraw nor chroma
        // to give — but there is already an opacity ladder, and fading into the
        // minimap's own ground is the same reading the board's veil gives.
        // Inventing a focus colour beside #3574f0 / #f5c85c would spend an
        // accent this surface has no room for.
        const ground = boardFocus?.role(item) === "ground";
        const baseOpacity =
          (sel || hi ? 0.9 : nodeColor ? 0.85 : 0.5) * (ground ? FOCUS_GROUND_ALPHA : 1);
        const x = offsetX + (item.x - ox) * scale;
        const y = offsetY + (item.y - oy) * scale;
        const w = item.w * scale;
        const h = item.h * scale;
        const stroke = sel ? "#3574f0" : "transparent";
        if (nodeShape(item.meta) === "ellipse") {
          return (
            <ellipse
              key={item.id}
              cx={x + w / 2}
              cy={y + h / 2}
              rx={w / 2}
              ry={h / 2}
              fill={baseFill}
              fillOpacity={baseOpacity}
              stroke={stroke}
              strokeWidth={0.5}
            />
          );
        }
        return (
          <rect
            key={item.id}
            x={x}
            y={y}
            width={w}
            height={h}
            fill={baseFill}
            fillOpacity={baseOpacity}
            stroke={stroke}
            strokeWidth={0.5}
            rx={1}
          />
        );
      })}
      <MinimapViewportRect
        store={store}
        screen={screen}
        scale={scale}
        offsetX={offsetX}
        offsetY={offsetY}
        ox={ox}
        oy={oy}
      />
    </svg>
    {menu.node}
    </>
  );
});

/** The camera indicator — the ONLY part of the minimap that tracks the
 *  per-frame camera, via direct attribute writes on a store subscription.
 *  Deliberately NOT `useSyncExternalStore`: uSES flushes each subscriber's
 *  sync re-render separately, so a second React subscriber turns one
 *  gesture frame into TWO commits (measured via `root.memoizedUpdaters`).
 *  Imperative writes keep the whole minimap at zero React work per frame. */
function MinimapViewportRect({
  store,
  screen,
  scale,
  offsetX,
  offsetY,
  ox,
  oy,
}: {
  readonly store: ViewportStore;
  readonly screen: { width: number; height: number };
  readonly scale: number;
  readonly offsetX: number;
  readonly offsetY: number;
  readonly ox: number;
  readonly oy: number;
}) {
  const ref = useRef<SVGRectElement>(null);
  useEffect(() => {
    const apply = () => {
      const el = ref.current;
      if (!el) return;
      const vp = store.get();
      const pan = panFor(vp, screen.width, screen.height);
      const zoom = vp.zoom;
      // Viewport rectangle in focal coords, projected onto the minimap.
      el.setAttribute("x", String(offsetX + ((0 - pan.x) / zoom - ox) * scale));
      el.setAttribute("y", String(offsetY + ((0 - pan.y) / zoom - oy) * scale));
      el.setAttribute("width", String((screen.width / zoom) * scale));
      el.setAttribute("height", String((screen.height / zoom) * scale));
    };
    apply();
    return store.subscribe(apply);
  }, [store, screen, scale, offsetX, offsetY, ox, oy]);
  return (
    <rect
      ref={ref}
      fill="rgba(53,116,240,0.08)"
      stroke="#3574f0"
      strokeWidth={1.5}
      rx={8}
      ry={8}
      style={{ pointerEvents: "none" }}
    />
  );
}
